import { useMemo, useRef, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { applyImport, buildExportWorkspace, fullSelection, treeItems } from '../../domain/importExport';
import { parseWorkspace } from '../../domain/workspace';
import { download, pickFile } from '../../lib/download';
import { slug, todayISO } from '../../lib/format';
import { t } from '../../i18n';
import type { Selection, TreeItem } from '../../domain/importExport';
import type { WorkspaceStore } from '../../domain/store';
import type { Workspace } from '../../types';

type Category = 'clubs' | 'athletes' | 'comps' | 'starters' | 'programs' | 'entries';

const CATEGORIES: readonly Category[] = ['clubs', 'athletes', 'comps', 'starters', 'programs', 'entries'];

const SECTION_TITLE: Record<
  Category,
  'ie.section.clubs' | 'ie.section.athletes' | 'ie.section.comps' | 'ie.section.starters' | 'ie.section.programs' | 'ie.section.entries'
> = {
  clubs: 'ie.section.clubs',
  athletes: 'ie.section.athletes',
  comps: 'ie.section.comps',
  starters: 'ie.section.starters',
  programs: 'ie.section.programs',
  entries: 'ie.section.entries',
};

function idsFor(sel: Selection, cat: Category): Set<string> {
  switch (cat) {
    case 'clubs':
      return sel.clubIds;
    case 'athletes':
      return sel.athleteIds;
    case 'comps':
      return sel.compIds;
    case 'starters':
      return sel.starterIds;
    case 'programs':
      return sel.programIds;
    case 'entries':
      return sel.entryIds;
  }
}

function categoryState(sel: Selection, items: readonly TreeItem[], cat: Category): 'checked' | 'indeterminate' | 'unchecked' {
  const all = items.map((i) => i.id);
  if (all.length === 0) return 'unchecked';
  const checked = all.filter((id) => idsFor(sel, cat).has(id)).length;
  if (checked === 0) return 'unchecked';
  if (checked === all.length) return 'checked';
  return 'indeterminate';
}

function selectionTotal(sel: Selection): number {
  return sel.clubIds.size + sel.athleteIds.size + sel.compIds.size + sel.starterIds.size + sel.programIds.size + sel.entryIds.size;
}

/**
 * File-explorer-style checkbox tree (macOS Finder "select what to copy"),
 * shared by "Import selection" and "Export selection". `source` is the
 * workspace to pick items *from* — the currently open workspace for export, the
 * parsed incoming file for import. Every category is independent — picking
 * an `Entry` doesn't pull in its competition/starter/programme, same as
 * picking a starter doesn't pull in its athletes.
 *
 * `sel`'s `Set`s are mutated in place (mirroring the vanilla version) rather
 * than rebuilt immutably — `bump()` is the version tick that tells React to
 * re-render after each mutation, the same pattern `useWorkspace` uses for the
 * main `WorkspaceStore`.
 */
function TreeSheet({
  mode,
  source,
  onConfirm,
  onClose,
}: {
  mode: 'import' | 'export';
  source: Workspace;
  onConfirm: (sel: Selection) => void;
  onClose: () => void;
}) {
  const selRef = useRef<Selection>(fullSelection(source));
  const [, setVersion] = useState(0);
  const bump = (): void => setVersion((v) => v + 1);
  const items = useMemo(() => treeItems(source), [source]);
  const [collapsed, setCollapsed] = useState<Set<Category>>(new Set());
  const sel = selRef.current;

  function setCategory(cat: Category, checked: boolean): void {
    const set = idsFor(sel, cat);
    set.clear();
    if (checked) for (const item of items[cat]) set.add(item.id);
    bump();
  }

  function toggleLeaf(cat: Category, id: string, checked: boolean): void {
    const set = idsFor(sel, cat);
    if (checked) set.add(id);
    else set.delete(id);
    bump();
  }

  function toggleCollapsed(cat: Category): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="bottom" className="gap-0 p-0">
        <SheetHeader>
          <SheetTitle>{t(mode === 'import' ? 'ie.title.import' : 'ie.title.export')}</SheetTitle>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4 p-4">
          {CATEGORIES.map((cat) => (
            <TreeSection
              key={cat}
              cat={cat}
              items={items[cat]}
              sel={sel}
              isCollapsed={collapsed.has(cat)}
              onToggleCollapse={() => toggleCollapsed(cat)}
              onSetCategory={(checked) => setCategory(cat, checked)}
              onToggleLeaf={(id, checked) => toggleLeaf(cat, id, checked)}
            />
          ))}
        </SheetBody>
        <SheetFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              for (const cat of CATEGORIES) setCategory(cat, true);
            }}
          >
            {t('ie.selectAll')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              for (const cat of CATEGORIES) setCategory(cat, false);
            }}
          >
            {t('ie.selectNone')}
          </Button>
          <span className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onConfirm(sel);
              onClose();
            }}
          >
            {t(mode === 'import' ? 'ie.confirm.import' : 'ie.confirm.export')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function TreeSection({
  cat,
  items,
  sel,
  isCollapsed,
  onToggleCollapse,
  onSetCategory,
  onToggleLeaf,
}: {
  cat: Category;
  items: readonly TreeItem[];
  sel: Selection;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSetCategory: (checked: boolean) => void;
  onToggleLeaf: (id: string, checked: boolean) => void;
}) {
  const state = categoryState(sel, items, cat);
  const hasItems = items.length > 0;

  return (
    <div>
      <div className="flex items-center gap-1.5 border-b pb-1.5">
        <button
          type="button"
          aria-expanded={!isCollapsed}
          aria-label={t(isCollapsed ? 'ie.expand' : 'ie.collapse')}
          disabled={!hasItems}
          onClick={onToggleCollapse}
          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary disabled:invisible"
        >
          <ChevronRightIcon className={cn('size-3.5 transition-transform', !isCollapsed && 'rotate-90')} />
        </button>
        <label className="flex flex-1 items-center gap-2.5 py-1 font-medium">
          <Checkbox
            checked={state === 'indeterminate' ? 'indeterminate' : state === 'checked'}
            disabled={!hasItems}
            onCheckedChange={(checked) => onSetCategory(checked === true)}
          />
          <span className="flex items-center gap-2 text-sm">
            {t(SECTION_TITLE[cat])} <Badge>{items.length || t('ie.section.empty')}</Badge>
          </span>
        </label>
      </div>
      {hasItems && !isCollapsed ? (
        <div className="flex flex-col gap-0.5 pt-1.5 pl-7">
          {items.map((item) => (
            <label className="flex items-center gap-2.5 py-1 text-sm text-muted-foreground" key={item.id}>
              <Checkbox checked={idsFor(sel, cat).has(item.id)} onCheckedChange={(checked) => onToggleLeaf(item.id, checked === true)} />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Presentational + confirm-logic component for the import/export tree sheet
 * — `stage` (built by the caller's button handlers, since picking the
 * incoming file is an async step that belongs in a click handler, not an
 * effect) says whether it's closed, showing the tree for an already-parsed
 * incoming file (import), or showing the tree over the current workspace
 * (export). Unlike the figure picker, this sheet has no state that needs to
 * survive being closed, so it's a plain conditionally-rendered component
 * rather than an imperative singleton.
 */
export type ImportExportStage = { kind: 'closed' } | { kind: 'import-tree'; incoming: Workspace } | { kind: 'export-tree' };

export function ImportExportSheet({
  store,
  toast,
  stage,
  onClose,
}: {
  store: WorkspaceStore;
  toast: (message: string) => void;
  stage: ImportExportStage;
  onClose: () => void;
}) {
  function handleImportConfirm(incoming: Workspace, sel: Selection): void {
    if (selectionTotal(sel) === 0) {
      toast(t('ie.import.noneSelected'));
      return;
    }
    const result = applyImport(store.workspace, incoming, sel);
    store.replace(result.workspace);
    toast(
      t('ie.import.done', {
        clubs: result.imported.clubs,
        athletes: result.imported.athletes,
        comps: result.imported.comps,
        starters: result.imported.starters,
        programs: result.imported.programs,
        entries: result.imported.entries,
        skipped: result.skipped,
      }),
    );
  }

  function handleExportConfirm(sel: Selection): void {
    if (selectionTotal(sel) === 0) {
      toast(t('ie.import.noneSelected'));
      return;
    }
    const exported = buildExportWorkspace(store.workspace, sel);
    void download(`${slug(store.workspace.meta.club, 'kuer-arbeitsbereich')}_${todayISO()}.kuer.json`, JSON.stringify(exported, null, 2), 'application/json').then(toast);
  }

  if (stage.kind === 'import-tree') {
    return <TreeSheet mode="import" source={stage.incoming} onConfirm={(sel) => handleImportConfirm(stage.incoming, sel)} onClose={onClose} />;
  }
  if (stage.kind === 'export-tree') {
    return <TreeSheet mode="export" source={store.workspace} onConfirm={handleExportConfirm} onClose={onClose} />;
  }
  return null;
}

/** Kicks off the async file-pick for "Import (Auswahl)…" — belongs in a
 *  click handler (not an effect) since it's a user-gesture-triggered async
 *  step; the caller stores the result as `ImportExportStage` state. */
export async function pickImportFile(toast: (message: string) => void): Promise<ImportExportStage | null> {
  const file = await pickFile('application/json,.json');
  if (!file) return null;
  try {
    return { kind: 'import-tree', incoming: parseWorkspace(JSON.parse(file.text)) };
  } catch {
    toast(t('ie.import.invalid'));
    return null;
  }
}
